import { useMemo } from 'react'
import { Badge } from '#app/components/ui/badge.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { formatPrice } from '#app/utils/currency.ts'
import {
	DUMPSTER_SIZES,
	matchListingToSize,
} from '../dumpster-size-calculator.logic.ts'

type Listing = {
	id: string
	title: string
	slug: string
	dailyRateCents: number | null
	weeklyRateCents: number | null
	monthlyRateCents: number | null
	subcategories: Array<{ slug: string; name: string }>
	ownerBusiness: {
		name: string
		slug: string
		phone: string | null
		website: string | null
	} | null
}

/**
 * Visual scale data for each dumpster size.
 * widthPct and heightPct are relative to the chart container,
 * calibrated so the sizes look proportionally correct next to
 * a standard pickup truck (roughly 19' L x 6' W x 6' H).
 */
const VISUAL_SCALE: Record<
	number,
	{ widthPct: number; heightPct: number; color: string; bgColor: string }
> = {
	10: {
		widthPct: 48,
		heightPct: 44,
		color: 'text-sky-700 dark:text-sky-300',
		bgColor: 'bg-sky-100 border-sky-300 dark:bg-sky-950/50 dark:border-sky-700',
	},
	15: {
		widthPct: 64,
		heightPct: 50,
		color: 'text-blue-700 dark:text-blue-300',
		bgColor:
			'bg-blue-100 border-blue-300 dark:bg-blue-950/50 dark:border-blue-700',
	},
	20: {
		widthPct: 88,
		heightPct: 56,
		color: 'text-indigo-700 dark:text-indigo-300',
		bgColor:
			'bg-indigo-100 border-indigo-300 dark:bg-indigo-950/50 dark:border-indigo-700',
	},
	30: {
		widthPct: 88,
		heightPct: 75,
		color: 'text-violet-700 dark:text-violet-300',
		bgColor:
			'bg-violet-100 border-violet-300 dark:bg-violet-950/50 dark:border-violet-700',
	},
	40: {
		widthPct: 88,
		heightPct: 100,
		color: 'text-purple-700 dark:text-purple-300',
		bgColor:
			'bg-purple-100 border-purple-300 dark:bg-purple-950/50 dark:border-purple-700',
	},
}

function getSizeStats(listings: Listing[], subcategorySlug: string) {
	const matched = listings.filter(
		(l) => matchListingToSize(l) === subcategorySlug,
	)
	const prices = matched
		.map((l) => l.weeklyRateCents ?? l.dailyRateCents)
		.filter((p): p is number => p != null && p > 0)

	return {
		lowPrice: prices.length > 0 ? Math.min(...prices) : null,
		highPrice: prices.length > 0 ? Math.max(...prices) : null,
	}
}

export function DumpsterSizeChart({ listings }: { listings: Listing[] }) {
	const sizeStats = useMemo(() => {
		const stats = new Map<string, ReturnType<typeof getSizeStats>>()
		for (const size of DUMPSTER_SIZES) {
			stats.set(
				size.subcategorySlug,
				getSizeStats(listings, size.subcategorySlug),
			)
		}
		return stats
	}, [listings])

	return (
		<div className="mt-10">
			<h3 className="text-lg font-semibold">Visual Size Comparison</h3>
			<p className="text-muted-foreground mt-1 text-sm">
				Dumpster sizes shown to scale relative to a standard pickup truck.
			</p>

			<div className="mt-6 space-y-5">
				{DUMPSTER_SIZES.map((size) => {
					const visual = VISUAL_SCALE[size.value]
					const stats = sizeStats.get(size.subcategorySlug)
					return (
						<div key={size.value} className="flex items-end gap-4">
							{/* Label column */}
							<div className="w-20 shrink-0 pb-1 text-right sm:w-24">
								<p className={`text-sm font-bold ${visual.color}`}>
									{size.label}
								</p>
								<p className="text-muted-foreground text-xs">
									{size.cubicYards} cu yd
								</p>
							</div>

							{/* Visual bar */}
							<div className="relative flex min-h-[3rem] flex-1 items-end">
								<div
									className={`flex items-end justify-between rounded border-2 px-3 py-1.5 transition-all ${visual.bgColor}`}
									style={{
										width: `${visual.widthPct}%`,
										minHeight: `${Math.max(visual.heightPct * 0.64, 40)}px`,
									}}
								>
									<span className="text-muted-foreground text-[11px] leading-tight">
										{size.dimensions}
									</span>
									<span className="text-muted-foreground ml-2 flex items-center gap-1 text-[11px] leading-tight">
										<Icon
											name="truck"
											className="inline-block size-3 opacity-60"
										/>
										{size.truckLoads}
									</span>
								</div>
							</div>

							{/* Price badge */}
							<div className="w-28 shrink-0 pb-1 text-right sm:w-36">
								{stats && stats.lowPrice != null ? (
									<Badge
										variant="secondary"
										className="text-xs font-medium text-green-700 dark:text-green-300"
									>
										{stats.lowPrice === stats.highPrice
											? formatPrice(stats.lowPrice)
											: `${formatPrice(stats.lowPrice)} – ${formatPrice(stats.highPrice!)}`}
									</Badge>
								) : (
									<span className="text-muted-foreground text-xs">
										Call for pricing
									</span>
								)}
							</div>
						</div>
					)
				})}

				{/* Pickup truck reference */}
				<div className="flex items-end gap-4">
					<div className="w-20 shrink-0 pb-1 text-right sm:w-24">
						<p className="text-muted-foreground text-sm font-medium">
							Pickup
						</p>
						<p className="text-muted-foreground text-xs">for scale</p>
					</div>
					<div className="relative flex min-h-[2.5rem] flex-1 items-end">
						<div
							className="flex items-center gap-2 rounded border-2 border-dashed border-gray-300 bg-gray-50 px-3 py-1.5 dark:border-gray-600 dark:bg-gray-900/50"
							style={{ width: '38%', minHeight: '36px' }}
						>
							<Icon
								name="truck"
								className="text-muted-foreground size-4"
							/>
							<span className="text-muted-foreground text-[11px]">
								Standard pickup truck bed (6.5&apos; x 5.5&apos;)
							</span>
						</div>
					</div>
					<div className="w-28 shrink-0 sm:w-36" />
				</div>
			</div>
		</div>
	)
}
